<div class="d-flex flex-column gap-3">
  <section v-if="standalone" class="entity-config-top mb-1 rounded-4 overflow-hidden shadow-sm">
    <div class="entity-config-top-hero px-2 px-md-3 py-3">
      <div class="d-flex justify-content-between align-items-start gap-3">
        <div>
          <h1 class="entity-page-title text-white fw-bold lh-sm mb-1">
            Workflow Management
          </h1>
          <p class="entity-page-subtitle text-white mb-0">
            Define signer order, entity types, and expected signer counts.
          </p>
        </div>

        <button
          type="button"
          class="btn btn-primary-dark text-xs rounded-3 px-3 py-2 shadow-sm"
          data-bs-toggle="modal"
          data-bs-target="#create_esigning_workflow_modal"
          @click="open_create_workflow_modal"
        >
          Create Workflow
        </button>
      </div>
    </div>
  </section>

  <section>
      <div v-if="workflows_loading" class="text-center text-secondary p-4">
        <div class="fw-semibold text-x mb-1">Loading workflows...</div>
      </div>

      <div v-else-if="!has_workflows" class="text-center text-secondary p-4">
        <div
          class="rounded-circle bg-primary-subtle text-primary d-inline-flex align-items-center justify-content-center mb-3 p-3 lh-1"
        >
          <i class="bi bi-diagram-3 fs-5"></i>
        </div>

        <div class="fw-semibold text-x mb-1">No workflows found</div>
        <div class="text-xs">
          Use the <b>Create Workflow</b> button to add your first workflow.
        </div>
      </div>

      <template v-else>
        <div
          class="entity-table-head secondary-color d-none d-lg-block rounded-3 mb-2 px-3 py-2 text-uppercase tracking-wide"
        >
          <div class="row g-2 align-items-center">
            <div class="col-lg-5">Workflow</div>
            <div class="col-lg-2">Steps</div>
            <div class="col-lg-2">Created</div>
            <div class="col-lg-3">Assigned Templates</div>
          </div>
        </div>

        <div class="d-flex flex-column gap-2">
          <article
            v-for="workflow in workflows"
            :key="workflow.id"
            class="entity-row-card bg-white rounded-4 border border-purple border-opacity-50 shadow-sm p-2"
          >
            <div class="row g-2 align-items-center">
              <div class="col-12 col-lg-5 d-flex align-items-center gap-3">
                <div
                  class="entity-type-icon entity-icon-surface rounded-3 d-inline-flex align-items-center justify-content-center flex-shrink-0"
                >
                  <i class="bi bi-diagram-3"></i>
                </div>

                <div class="min-w-0">
                  <div class="fw-bold text-x text-truncate">
                    {{ workflow.name }}
                  </div>
                  <div class="text-secondary text-xs text-wrap text-break">
                    {{ workflow.description || workflow_entity_type_summary(workflow) }}
                  </div>
                </div>
              </div>

              <div class="col-12 col-sm-6 col-lg-2 text-xs text-secondary">
                {{ workflow_step_summary(workflow) }}
              </div>

              <div class="col-12 col-sm-6 col-lg-2 text-xs text-secondary">
                <i class="bi bi-calendar3 me-2"></i>
                {{ format_date(workflow.created_at) }}
              </div>

              <div class="col-12 col-sm-6 col-lg-2 text-xs text-secondary">
                {{ workflow_template_summary(workflow) }}
              </div>
            </div>
          </article>
        </div>
      </template>
  </section>

  <teleport to="body">
    <div
      class="modal fade"
      id="create_esigning_workflow_modal"
      tabindex="-1"
      aria-labelledby="create_esigning_workflow_modal_label"
      aria-hidden="true"
    >
      <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
        <div class="modal-content bg-white rounded-4 p-0 shadow-lg border-0 overflow-hidden">
          <div class="modal-header border-bottom-0 cute-header px-4 py-3 shadow-lg">
            <div class="d-flex flex-column text-white">
              <p class="modal-title fw-bold mb-1" id="create_esigning_workflow_modal_label">
                Create Workflow
              </p>
              <small class="opacity-75">
                Define the workflow name, description, and signer steps.
              </small>
            </div>

            <button
              type="button"
              class="modal-btn-close btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
            ></button>
          </div>

          <div class="modal-body px-3 px-md-4 py-4 bg-white">
              <div class="row g-3 mb-4">
                <div class="col-12 col-md-6">
                  <label class="form-label text-xxs text-uppercase fw-bold text-secondary mb-2">
                    Workflow Name
                  </label>
                  <input
                    v-model.trim="workflow_form.name"
                    type="text"
                    class="form-control text-xs rounded-4 py-2"
                    placeholder="e.g. Supplier Contract Approval"
                  />
                </div>

                <div class="col-12 col-md-6">
                  <label class="form-label text-xxs text-uppercase fw-bold text-secondary mb-2">
                    Description
                  </label>
                  <input
                    v-model.trim="workflow_form.description"
                    type="text"
                    class="form-control text-xs rounded-4 py-2"
                    placeholder="Two-step signing flow"
                  />
                </div>
              </div>

              <div class="border-top pt-4">
                <div class="d-flex justify-content-between align-items-center gap-3 mb-3 flex-wrap">
                  <div>
                    <div class="fw-bold d-flex align-items-center gap-2 text-dark">
                      <span class="d-inline-flex align-items-center justify-content-center rounded-3 bg-light text-purple esign-workflow-section-icon">
                        <i class="bi bi-bag"></i>
                      </span>
                      <span>Workflow Steps</span>
                    </div>
                    <div class="text-xxs text-secondary mt-1">
                      Set the signing order and expected signer count per step.
                    </div>
                  </div>

                  <button
                    type="button"
                    class="btn btn-sm rounded-4 text-xs px-3 py-2 fw-semibold esign-workflow-add-btn"
                    @click="add_step"
                  >
                    <i class="bi bi-plus-lg me-1"></i>
                    Add Step
                  </button>
                </div>

                <div class="d-flex flex-column gap-3">
                  <div
                    v-for="(step, index) in workflow_steps"
                    :key="`esign-step-${index}`"
                    class="border rounded-4 bg-light-subtle p-3"
                  >
                    <div class="row g-3 align-items-center align-items-md-start">
                      <div class="col-12 col-md-auto d-flex justify-content-start justify-content-md-center pt-md-4">
                        <div class="d-inline-flex align-items-center justify-content-center fw-semibold bg-white esign-workflow-step-number">
                          {{ index + 1 }}
                        </div>
                      </div>

                      <div class="col-12 col-md-5">
                        <label class="form-label text-xxs text-uppercase fw-bold text-secondary mb-1">
                          Entity Type
                        </label>
                        <select
                          v-model="step.entity_type_id"
                          class="form-select form-select-sm text-xs rounded-4 py-2"
                          :disabled="entity_types_loading"
                        >
                          <option value="" disabled>
                            {{
                              entity_types_loading
                                ? 'Loading entity types...'
                                : entity_type_options.length
                                  ? 'Select entity type'
                                  : 'No entity types available'
                            }}
                          </option>
                          <option
                            v-for="option in entity_type_options"
                            :key="option.value"
                            :value="option.value"
                          >
                            {{ option.label }}
                          </option>
                        </select>
                      </div>

                      <div class="col-12 col-md-5">
                        <label class="form-label text-xxs text-uppercase fw-bold text-secondary mb-1">
                          Expected Signers
                        </label>
                        <input
                          v-model.number="step.expected_signer_count"
                          type="number"
                          min="1"
                          class="form-control form-control-sm text-xs rounded-4 py-2"
                        />
                      </div>

                      <div class="col-12 col-md d-flex justify-content-md-end">
                        <button
                          type="button"
                          class="btn btn-link text-danger text-xs text-decoration-none px-0 fw-medium"
                          @click="remove_step(index)"
                          :disabled="workflow_steps.length === 1"
                        >
                          <i class="bi bi-trash me-1"></i>
                          Remove
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
          </div>

          <div class="modal-footer border-top px-3 px-md-4 py-3 d-flex justify-content-end gap-2 bg-white">
            <button
              type="button"
              class="btn btn-link text-decoration-none text-secondary fw-semibold px-2"
              data-bs-dismiss="modal"
            >
              Cancel
            </button>
            <button
              type="button"
              class="btn btn-primary-dark btn-sm rounded-4 text-xs px-4 py-2 shadow-sm fw-semibold"
              @click="create_workflow"
              :disabled="workflow_running"
            >
              {{ workflow_running ? 'Creating...' : 'Save Workflow' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </teleport>
</div>
